import i18next, {
  changeLanguage,
  use,
  type i18n as I18n,
  type InitOptions,
  type BackendModule,
  type ReadCallback,
  type Resource,
  type ResourceKey,
  type Services,
  type TFunction as I18nextTFunction,
  type TOptions,
  type ResourceLanguage,
  type KeyPrefix,
  type ParseKeys,
} from 'i18next';
import {
  type UseTranslationOptions,
  initReactI18next,
  useTranslation as useTranslationBase,
} from 'react-i18next';

export type { LegacyStringTFunction, TFunction } from './i18next-types';
export { Translation } from './i18next-translation';
export { Trans } from './i18next-trans';

import type { LegacyStringTFunction } from './i18next-types';

type UseTranslationParams = Parameters<typeof useTranslationBase>;

/** react-i18next の UseTranslationResponse はタプル兼オブジェクトのため、利用する形だけ明示する */
export type AppUseTranslationResponse = {
  t: LegacyStringTFunction;
  i18n: I18n;
  ready: boolean;
};

/**
 * @see https://www.locize.com/blog/i18next-typescript-selector-api
 */
export function useTranslation(
  ns?: UseTranslationParams[0],
  options?: UseTranslationParams[1],
): AppUseTranslationResponse {
  const { t: innerT, i18n, ready } = useTranslationBase(ns, options);
  return {
    t: innerT as unknown as LegacyStringTFunction,
    i18n,
    ready,
  };
}

export { toI18nextT } from './i18next-helpers';

/**
 * i18next 直 import の t 代替（enableSelector 下でも string key を使う）
 */
const rawT = i18next.t.bind(i18next) as (
  key: string | string[] | ((src: never) => string),
  options?: string | TOptions,
  rest?: TOptions,
) => string;

export const legacyT = ((key, defaultValueOrOptions, options) => {
  if (typeof key === 'function') {
    return rawT(key, defaultValueOrOptions as TOptions | undefined);
  }
  if (typeof defaultValueOrOptions === 'string') {
    return rawT(key, { defaultValue: defaultValueOrOptions, ...options });
  }
  return rawT(key, defaultValueOrOptions, options);
}) as LegacyStringTFunction & {
  (selector: (src: never) => string, options?: TOptions): string;
};

// i18next関連の機能をエクスポート
export {
  changeLanguage,
  i18next,
  initReactI18next,
  type UseTranslationOptions,
  use,
  type InitOptions,
  type BackendModule,
  type ReadCallback,
  type Resource,
  type ResourceKey,
  type Services,
  type I18nextTFunction,
  type TOptions,
  type ResourceLanguage,
  type KeyPrefix,
  type ParseKeys,
};
